import type { FlagType } from 'bc-schemas';
import type { Static } from 'runtypes';
import {
  Boolean,
  InstanceOf,
  Literal,
  Null,
  Number,
  Optional,
  Record,
  String,
  Union,
} from 'runtypes';
import type { AccountSavedType } from './accounts';

type FeatureFlagKey =
  | 'CLICKSTREAM_SESSION_TRACKING_ENABLED'
  | 'CLICKSTREAM_OTHER_TRACKING_ENABLED'
  | 'API_RATE_LIMIT_ENABLED'
  | 'WS_RATE_LIMIT_ENABLED'
  | 'BRIEF_HISTORY_CACHE_ENABLED'
  | 'ARTIFACT_CACHING_ENABLED'
  | 'LOG_TRANSACTION_BYTE_THRESHOLD_ENABLED'
  | 'WORKER_THREAD_ENABLED'
  | 'DOCLING_ENABLED'
  | 'AI_ASSIST_ENABLED'
  | 'AI_ASSIST_RAG_ENABLED'
  | 'AI_ASSIST_RERANK_ENABLED'
  | 'AI_ASSIST_ADMIN_MODE_ENABLED'
  | 'AI_ASSIST_DYNAMIC_RATE_LIMITS'
  | 'AI_ASSIST_CHAT_HISTORY_ENABLED'
  | 'AI_CARD_SHORTHAND_ENABLED'
  | 'AI_CARDIFY_ENABLED'
  | 'AI_ORDER_CARD_LIST_SUMMARIES_ENABLED'
  | 'AI_SEMANTIC_SEARCH_ENABLED'
  | 'AI_HYBRID_SEARCH_ENABLED'
  | 'CLICKSTREAM_AI_ASSIST_ENABLED'
  | 'CLICKSTREAM_AI_ASSIST_CONTENT_ENABLED'
  | 'MAPS_API_ICON_CACHE_ENABLED'
  | 'MQ_TASKING_ENABLED'
  | 'INDEXED_DB_ENABLED'
  | 'EMBED_CACHING'
  | 'NGA_MAPS_ENABLED'
  | 'NORMALIZE_PDF_UPLOAD_ENABLED'
  | 'ONEBRIEF_API_ENABLED'
  | 'AI_ASSIST_LOGGING_CONTENT_ENABLED'
  | 'CSV_REUPLOAD_ENABLED'
  | 'API_CARD_UPSERT_ENABLED'
  | 'DOCUMENT_INLINE_EDITING_ENABLED'
  | 'ORDER_PAGED_EDITOR_ENABLED'
  | 'ORDER_NODE_VIEW_VIRTUALIZATION_ENABLED'
  | 'DATA_TABLES_P2_ENABLED_GA'
  | 'DATA_TABLES_P3_COMPUTATIONS_ENABLED'
  | 'DATA_TABLES_P3_INLINE_EDITING_ENABLED'
  | 'LEGACY_DOC_EXPORT_TO_PDF_ENABLED'
  | 'COLLAB_STALE_WHEN_REPLAY_FAILURE_ENABLED'
  | 'RATE_LIMITER_V2_ENABLED'
  | 'RATE_LIMITER_V2_DRY_RUN_ENABLED'
  | 'TIMELINES_V2_ENABLED_GA'
  | 'TIMELINE_PRECISION_IMPROVEMENTS_ENABLED'
  | 'TIMELINE_VISUALIZATION_CONTROLS_ENABLED'
  | 'ENHANCED_LIBRARY_SEARCH_ENABLED'
  | 'ADMIN_OPLOG_ANALYSIS_ENABLED'
  | 'TENANT_MGMT'
  | 'TABLES_IN_DOCUMENTS'
  | 'MAPTILE_CLIENT_CACHE_ENABLED'
  | 'MSS_INTEGRATION_ENABLED'
  | 'MULTILEVEL_LIST_FORMATTING_ENABLED'
  | 'FOUNDRY_OUTBOUND_SYNC_ENABLED'
  | 'FOUNDRY_SYNC_ON_WORKER'
  | 'FOUNDRY_SYNC_VERBOSE'
  | 'FOUNDRY_ONTOLOGY_AUTO_REFRESH_ENABLED'
  | 'FOUNDRY_INBOUND_SYNC_ENABLED'
  | 'FOUNDRY_ONTOLOGY_UPLOAD_ONLY'
  | 'REFRESHED_HOMEPAGE'
  | 'ASYNC_NOTIFICATION_SHADOW_MODE_ENABLED'
  | 'ASYNC_NOTIFICATIONS_ENABLED'
  | 'WS_REBALANCE_ENABLED'
  | 'USE_ACCOUNT_UUID'
  | 'ORBITS_ENABLED'
  | 'PLAN_SNAPSHOTS_ENABLED'
  | 'SSO_ACCOUNT_LINKING_ENABLED'
  | 'OIDC_SERVICE_ACCOUNT_ACTIONS'
  | 'AI_AGENTIC_MODE_ENABLED'
  | 'FRAME_CLASSIFICATION_ENABLED'
  | 'AE_MAP_UNITS_ENABLED'
  | 'WARGAME_ENDING_CONDITIONS_ENABLED'
  | 'PICKER_V2_FACETED_BROWSE';

const TargetingModeRunType = Union(Literal('global'), Literal('accounts'));
type TargetingMode = Static<typeof TargetingModeRunType>;

interface FeatureFlagDefinition {
  /**
   * Used to describe the _impact_ and _usage_ of enabling or disabling the flag.
   */
  description: string;
  /**
   * Used to initialize new flags in the database and as a fallback if a flag is not loaded or found.
   */
  fallbackValue: boolean;
  targetingMode?: TargetingMode;
  type: FlagType;
}

type FeatureFlagDefinitionsType = {
  [K in FeatureFlagKey]: FeatureFlagDefinition;
};

const FEATURE_FLAG_DEFINITIONS: FeatureFlagDefinitionsType = {
  MAPTILE_CLIENT_CACHE_ENABLED: {
    description: 'Enable/disable using a client-side cache to store map tiles',
    fallbackValue: false,
    type: 'system',
  },
  WARGAME_ENDING_CONDITIONS_ENABLED: {
    description:
      'Show the wargame Configure-step Ending Conditions authoring UI (max sim time, max ticks, combat-power/casualty thresholds). Off by default — runs are ended manually via the dashboard "End Game" button.',
    fallbackValue: false,
    type: 'product',
  },
  MSS_INTEGRATION_ENABLED: {
    description:
      'Enable/disable the Foundry/MSS integration for syncing planning data to Palantir Foundry',
    fallbackValue: false,
    type: 'system',
  },
  CLICKSTREAM_SESSION_TRACKING_ENABLED: {
    description: 'Enable/disable clickstream session tracking',
    fallbackValue: true,
    type: 'system',
  },
  CLICKSTREAM_OTHER_TRACKING_ENABLED: {
    description: 'Enable/disable clickstream other tracking',
    fallbackValue: true,
    type: 'system',
  },
  CLICKSTREAM_AI_ASSIST_ENABLED: {
    description: 'Enable/disable clickstream logging AI Assist events',
    fallbackValue: false,
    type: 'system',
  },
  CLICKSTREAM_AI_ASSIST_CONTENT_ENABLED: {
    description: 'Enable/disable clickstream logging artifact content for AI Assist',
    fallbackValue: false,
    type: 'system',
  },
  API_RATE_LIMIT_ENABLED: {
    description: 'Enable/disable API rate limiting',
    fallbackValue: false,
    type: 'system',
  },
  WS_RATE_LIMIT_ENABLED: {
    description: 'Enable/disable websocket rate limiting',
    fallbackValue: true,
    type: 'system',
  },
  BRIEF_HISTORY_CACHE_ENABLED: {
    description:
      'Enable/disable caching the brief activity history on plan dashboard (expensive query on big plans)',
    fallbackValue: false,
    type: 'system',
  },
  ARTIFACT_CACHING_ENABLED: {
    description: 'Enable/disable server caching of mutated artifacts',
    fallbackValue: true,
    type: 'system',
  },
  LOG_TRANSACTION_BYTE_THRESHOLD_ENABLED: {
    description: 'Enable/disable server logging of transaction byte size threshold',
    fallbackValue: false,
    type: 'system',
  },
  WORKER_THREAD_ENABLED: {
    description:
      'Enable/disable worker thread execution for heavy operations (plan/artifact duplication)',
    fallbackValue: true,
    type: 'system',
  },
  DOCLING_ENABLED: {
    description: 'Enable/disable Docling for pdf text extraction',
    fallbackValue: false,
    type: 'product',
  },
  AI_ASSIST_ENABLED: {
    description: 'Enable/disable AI Assist feature',
    fallbackValue: false,
    type: 'system',
  },
  AI_AGENTIC_MODE_ENABLED: {
    description:
      'Enable/disable access to the agentic AI assist mode (AGUI-based streaming with /messages routing)',
    fallbackValue: false,
    type: 'system',
  },
  AI_CARD_SHORTHAND_ENABLED: {
    description: 'Enable/disable AI Card Shorthand feature',
    fallbackValue: false,
    type: 'product',
  },
  AI_CARDIFY_ENABLED: {
    description: 'Enable/disable AI Assist Cardify prompt feature',
    fallbackValue: false,
    type: 'product',
  },
  AI_ORDER_CARD_LIST_SUMMARIES_ENABLED: {
    description: 'Enable/disable AI Order Card List Summaries feature',
    fallbackValue: false,
    type: 'product',
  },
  AI_SEMANTIC_SEARCH_ENABLED: {
    description: 'Enable/disable AI Semantic Search feature',
    fallbackValue: false,
    type: 'product',
  },
  AI_ASSIST_RAG_ENABLED: {
    description:
      'Enable/disable improved AI Assist context via RAG, requires the embedding service',
    fallbackValue: false,
    type: 'system',
  },
  AI_ASSIST_RERANK_ENABLED: {
    description:
      'Enable/disable reranking of retrieved context for AI Assist (server /api/ai/rerank)',
    fallbackValue: false,
    type: 'product',
  },
  AI_ASSIST_ADMIN_MODE_ENABLED: {
    description: 'Enable/disable user ability to set admin mode in AI Assist',
    fallbackValue: false,
    type: 'system',
  },
  AI_ASSIST_DYNAMIC_RATE_LIMITS: {
    description: 'Enable/disable dynamic AI Assist rate limiting (requests & tokens)',
    fallbackValue: false,
    type: 'product',
  },
  AI_ASSIST_CHAT_HISTORY_ENABLED: {
    description: 'Enable/disable AI Assist Chat History feature',
    fallbackValue: false,
    type: 'system',
  },
  AI_HYBRID_SEARCH_ENABLED: {
    description: 'Enable/disable AI Hybrid Search feature',
    fallbackValue: false,
    type: 'product',
  },
  MAPS_API_ICON_CACHE_ENABLED: {
    description: 'Enable/disable Caching For Maps API Icons',
    fallbackValue: true,
    type: 'system',
  },
  MQ_TASKING_ENABLED: {
    description: 'Enable/disable task execution in external process via message queue',
    fallbackValue: false,
    type: 'system',
  },
  INDEXED_DB_ENABLED: {
    description: 'Enable/disable Caching in WebWorker using IndexedDB',
    fallbackValue: true,
    type: 'system',
  },
  EMBED_CACHING: {
    description:
      'Replaces map embeds with cached images to speed up presentations (excludes framed whiteboard maps).',
    fallbackValue: false,
    type: 'product',
  },
  NGA_MAPS_ENABLED: {
    description: 'Enable/disable NGA map layers',
    fallbackValue: false,
    type: 'product',
  },
  NORMALIZE_PDF_UPLOAD_ENABLED: {
    description: 'Enable/disable normalization of uploaded PDFs to reduce hash variability',
    fallbackValue: true,
    type: 'product',
  },
  ONEBRIEF_API_ENABLED: {
    description: 'Enable/disable the Onebrief external API',
    fallbackValue: false,
    type: 'system',
  },
  AI_ASSIST_LOGGING_CONTENT_ENABLED: {
    description: 'Enable/disable logging user content for AI Assist',
    fallbackValue: false,
    type: 'system',
  },
  CSV_REUPLOAD_ENABLED: {
    description: 'Enable/disable the ability to re-upload CSV files to update existing data tables',
    fallbackValue: false,
    type: 'system',
  },
  API_CARD_UPSERT_ENABLED: {
    description: 'Enable/disable the API endpoint for upserting cards',
    fallbackValue: false,
    type: 'system',
  },
  DOCUMENT_INLINE_EDITING_ENABLED: {
    description:
      'Enable/disable double-click to edit cards inline embedded in documents rather than opening the side panel.',
    fallbackValue: false,
    type: 'product',
  },
  ORDER_PAGED_EDITOR_ENABLED: {
    description: 'Enable/disable visual paper pages in the Order editor.',
    fallbackValue: false,
    type: 'product',
  },
  ORDER_NODE_VIEW_VIRTUALIZATION_ENABLED: {
    description: 'Enable/disable viewport-based virtualization for heavy Order node views.',
    fallbackValue: false,
    type: 'product',
  },
  DATA_TABLES_P2_ENABLED_GA: {
    description: 'Enable/disable DataTable phase 2 support',
    fallbackValue: true,
    type: 'product',
  },
  DATA_TABLES_P3_COMPUTATIONS_ENABLED: {
    description: 'Enable/disable DataTable phase 3 computations support',
    fallbackValue: false,
    type: 'product',
  },
  DATA_TABLES_P3_INLINE_EDITING_ENABLED: {
    description: 'Enable/disable DataTable phase 3 inline editing support',
    fallbackValue: false,
    type: 'product',
  },
  RATE_LIMITER_V2_ENABLED: {
    description: 'Enable/disable the v2 rate limiter system',
    fallbackValue: false,
    type: 'system',
  },
  RATE_LIMITER_V2_DRY_RUN_ENABLED: {
    description: 'Enable/disable the v2 rate limiter dry run mode',
    fallbackValue: false,
    type: 'system',
  },
  LEGACY_DOC_EXPORT_TO_PDF_ENABLED: {
    description: 'Enable/disable the legacy document export to PDF feature',
    fallbackValue: false,
    type: 'product',
  },
  COLLAB_STALE_WHEN_REPLAY_FAILURE_ENABLED: {
    description: 'Enable/disable throwing stale data toast when OT replay fails in Collab',
    fallbackValue: true,
    type: 'system',
  },
  TIMELINES_V2_ENABLED_GA: {
    description: 'Enable/disable timelines version 2 features',
    fallbackValue: true,
    type: 'product',
  },
  TIMELINE_PRECISION_IMPROVEMENTS_ENABLED: {
    description: 'Enable/disable timelines v2 phase 2.1 features',
    fallbackValue: false,
    type: 'product',
  },
  TIMELINE_VISUALIZATION_CONTROLS_ENABLED: {
    description: 'Enable/disable timelines v2 phase 2.2 features',
    fallbackValue: false,
    type: 'product',
  },
  ENHANCED_LIBRARY_SEARCH_ENABLED: {
    description: 'Enable/disable the enhanced library search feature',
    fallbackValue: false,
    type: 'product',
  },
  ADMIN_OPLOG_ANALYSIS_ENABLED: {
    description: 'Enable/disable oplog analysis dashboard for SREs and admins',
    fallbackValue: false,
    type: 'system',
  },
  TENANT_MGMT: {
    description: 'Enable/disable tenant management in admin panel',
    fallbackValue: false,
    type: 'system',
  },
  TABLES_IN_DOCUMENTS: {
    description: 'Enable/disable embedding data tables in documents',
    fallbackValue: false,
    type: 'product',
  },
  MULTILEVEL_LIST_FORMATTING_ENABLED: {
    description:
      'Allows users to customize bullet characters per indent level and control text wrap alignment in bulleted lists',
    fallbackValue: true,
    type: 'product',
  },
  FOUNDRY_OUTBOUND_SYNC_ENABLED: {
    description:
      'Enable/disable the outbound sync loop that processes the Foundry sync outbox and pushes data to Palantir Foundry',
    fallbackValue: false,
    type: 'system',
  },
  FOUNDRY_SYNC_ON_WORKER: {
    description:
      'Run the Foundry outbound stream consumer on worker pods instead of bc-server. Toggle at runtime to migrate sync processing between processes.',
    fallbackValue: false,
    type: 'system',
  },
  FOUNDRY_SYNC_VERBOSE: {
    description:
      'Enable verbose logging for the Foundry sync pipeline (tick summaries, skipped jobs, success details)',
    fallbackValue: false,
    type: 'system',
  },
  FOUNDRY_ONTOLOGY_AUTO_REFRESH_ENABLED: {
    description:
      'Enable background auto-refresh of cached Foundry ontology metadata when it is older than 24 hours',
    fallbackValue: false,
    type: 'system',
  },
  FOUNDRY_INBOUND_SYNC_ENABLED: {
    description:
      'Enable/disable inbound sync from Palantir Foundry via WebSocket subscriptions. Worker pods open WebSocket connections and process incoming object changes.',
    fallbackValue: false,
    type: 'system',
  },
  FOUNDRY_ONTOLOGY_UPLOAD_ONLY: {
    description:
      'When enabled, ontology metadata is only populated via explicit JSON upload. When disabled, fetched ontology is auto-stored on validation and refresh.',
    fallbackValue: true,
    type: 'product',
  },
  REFRESHED_HOMEPAGE: {
    description: 'Enable/disable the refreshed homepage experience',
    fallbackValue: false,
    type: 'product',
  },
  ASYNC_NOTIFICATION_SHADOW_MODE_ENABLED: {
    description:
      'Enable/disable async notification shadow mode: dual writes to new and old notification system',
    fallbackValue: false,
    type: 'product',
  },
  ASYNC_NOTIFICATIONS_ENABLED: {
    description: 'Enable/disable async notification pipeline.',
    fallbackValue: false,
    type: 'product',
  },
  WS_REBALANCE_ENABLED: {
    description:
      'Enable/disable server-initiated socket rebalancing to redistribute clients across server pods',
    fallbackValue: false,
    type: 'system',
  },
  USE_ACCOUNT_UUID: {
    description:
      'When enabled, the system uses account UUIDs instead of sequential account IDs for API requests and frontend references',
    fallbackValue: false,
    type: 'product',
  },
  ORBITS_ENABLED: {
    description:
      'Enable/disable satellite orbit ground traces on maps. Originally released under beta mode in Nov 2024, migrated to feature flag in Mar 2026.',
    fallbackValue: false,
    type: 'product',
  },
  PLAN_SNAPSHOTS_ENABLED: {
    description:
      'Enable/disable the Plan Snapshots feature for creating locked snapshots of a plan',
    fallbackValue: false,
    type: 'product',
  },
  SSO_ACCOUNT_LINKING_ENABLED: {
    description: 'Enable/disable SSO account linking feature',
    fallbackValue: false,
    type: 'system',
  },
  OIDC_SERVICE_ACCOUNT_ACTIONS: {
    description: 'Enable/disable user management actions performed by OIDC client service accounts',
    fallbackValue: false,
    type: 'system',
  },
  FRAME_CLASSIFICATION_ENABLED: {
    description: 'Enables the ability to toggle on/off classification badges for whiteboard frames',
    fallbackValue: false,
    type: 'product',
  },
  AE_MAP_UNITS_ENABLED: {
    description:
      'Enable/disable the right-click "Add AtomEngine Unit" menu item in the brief map. Off by default in v1; gates the entire scenario-builder integration UI surface.',
    fallbackValue: false,
    type: 'system',
  },
  PICKER_V2_FACETED_BROWSE: {
    description:
      'Enable the v2 faceted-browse AE entity picker (segmented affiliation switcher, facet rail, result-row chips) per laymann/specs/entity-picker-ux.md. When off, the existing v1 picker continues to be served. v1 stays the default until v2 is stable.',
    // Temporarily true for Cody's local dev preview of picker v2 iters 1+2.
    // Revert to `false` before any push upstream — v1 stays the default
    // until v2 is stable per the description above.
    fallbackValue: true,
    type: 'product',
  },
} as const;

const FeatureFlagUpdateRunType = Record({
  key: String,
  enabled: Optional(Boolean),
  targetingMode: Optional(TargetingModeRunType),
});

const FeatureFlagCreateRunType = FeatureFlagUpdateRunType.And(
  Record({
    enabled: Boolean,
    targetingMode: TargetingModeRunType,
    deleted: Boolean,
    createdAt: InstanceOf(Date),
    createdBy: Number,
    createdByUuid: Optional(Union(String, Null)),
    updatedAt: InstanceOf(Date),
    updatedBy: Number,
    updatedByUuid: Optional(Union(String, Null)),
  })
);

type FeatureFlagUpdate = Static<typeof FeatureFlagUpdateRunType>;

const FeatureFlagRunType = FeatureFlagCreateRunType.And(
  Record({
    accounts: Number,
  })
);

type FeatureFlag = Static<typeof FeatureFlagRunType> & {
  key: FeatureFlagKey;
};

type FeatureFlagAccount = {
  accountId: number;
  accountUuid?: string | null;
  createdAt: Date;
  createdBy: number;
  account: AccountSavedType;
};

function getFeatureFlagDefinition(flagName: FeatureFlagKey): FeatureFlagDefinition {
  return FEATURE_FLAG_DEFINITIONS[flagName];
}

function getFeatureFlagKeys(): FeatureFlagKey[] {
  return Object.keys(FEATURE_FLAG_DEFINITIONS) as FeatureFlagKey[];
}

export {
  FEATURE_FLAG_DEFINITIONS,
  FeatureFlagCreateRunType,
  FeatureFlagUpdateRunType,
  FeatureFlagRunType,
  getFeatureFlagDefinition,
  getFeatureFlagKeys,
};

export type {
  FeatureFlag,
  FeatureFlagUpdate,
  FeatureFlagDefinition,
  FeatureFlagKey,
  FeatureFlagAccount,
};
